import type { FC } from 'react';
import React, { useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { InvestAddressRegistrationField } from '@alfa-bank/site-invest-address-registration-field';
import { InvestPassportSN } from '@alfa-bank/site-invest-passport-sn';
import { FormDesktop as Form } from '@alfa-bank/site-form/desktop';
import { TextDesktop as Text } from '@alfa-bank/site-text-v2/desktop';
import { Aligner } from '@alfa-bank/site-aligner';
import { SecurityWarrantyBlockDesktop as SecurityWarrantyBlock } from '@alfa-bank/site-security-warranty-block/desktop';
import type { TFormValues } from '@alfa-bank/site-form/src/models';
import type { FormApi } from 'final-form';
import { FormProgressBarV2Desktop } from '@alfa-bank/site-form-progress-bar-v2/desktop';

import type { IAddressAdditionFormValues, TDispatch } from '../../store';
import {
    selectShowProgressBar,
    selectAddressAddition,
    selectAppMeta,
    submitAddressAddition,
    setFormFieldsAddressAddition,
} from '../../store';
import { AnalyticsSpy } from '../../components/analytics-spy';
import type { TFormSubmitCallback } from '../../models/common-types';
import { ADDRESS, PASSPORT } from '../../config';
import { getFormatPassportSN } from '../../helpers/transform';
import { LoadingBlockDesktop } from '../../components/loading-block/desktop';

import { defaultAddressAdditionSettings } from './config/default-values';
import type { TAddressAdditionStepDesktop } from './model';
import { analyticsAddressAdditionSmev } from './analytics';

export const AddressAdditionStepDesktop: FC<TAddressAdditionStepDesktop> = ({
    addressSettings = defaultAddressAdditionSettings.addressSettings,
    passportSettings = defaultAddressAdditionSettings.passportSettings,
    progressBarSettings = defaultAddressAdditionSettings.progressBarSettings,
    loadingText = defaultAddressAdditionSettings.loadingText,
    loadingTextIconSrc,
    securityWarrantyBlockSettings = defaultAddressAdditionSettings.securityWarrantyBlockSettings,
    title,
}) => {
    const showProgressBar = useSelector(selectShowProgressBar);
    const state = useSelector(selectAddressAddition);
    const stateAppMeta = useSelector(selectAppMeta);
    const dispatch = useDispatch<TDispatch>();

    const handleOnSubmit = useCallback(
        async (
            _: TFormValues,
            formApi: FormApi<TFormValues>,
            finishSubmitCallback: TFormSubmitCallback
        ) => {
            const fields = formApi.getState().values as unknown as IAddressAdditionFormValues;

            dispatch(setFormFieldsAddressAddition(fields));
            await dispatch(submitAddressAddition());

            finishSubmitCallback();
        },
        [dispatch]
    );

    return (
        <Form
            analytics={{
                submit: analyticsAddressAdditionSmev.formSubmitSuccess,
                submitOnFail: analyticsAddressAdditionSmev.formSubmitFail,
            }}
            initialValues={{
                [PASSPORT]: getFormatPassportSN(stateAppMeta[PASSPORT]),
                [ADDRESS]: stateAppMeta[ADDRESS]?.fullName,
                [`${ADDRESS}-dadata`]: stateAppMeta[ADDRESS],
            }}
            onSubmit={handleOnSubmit}
            view="without-shadow"
        >
            <AnalyticsSpy analytics={analyticsAddressAdditionSmev} />
            {title && (
                <Aligner styler={{ marginBottom: 24 }}>
                    <Text styler={{ marginTop: 0, marginBottom: 0 }} tag="h2" view="medium">
                        {title}
                    </Text>
                </Aligner>
            )}
            {showProgressBar && (
                <FormProgressBarV2Desktop
                    caption={progressBarSettings.caption}
                    currentStepName={progressBarSettings.currentStepName}
                    hatchValue={progressBarSettings.hatchValue}
                    header={progressBarSettings.header}
                    line={progressBarSettings.line}
                    maxStep={progressBarSettings.maxStep}
                    nextStepName={progressBarSettings.nextStepName}
                    steps={progressBarSettings.steps}
                    styler={{ marginBottom: 24 }}
                    value={progressBarSettings.value}
                />
            )}
            <InvestPassportSN
                {...passportSettings}
                disabled
                isRequired={false}
                name={PASSPORT}
                styler={{ marginBottom: 24 }}
            />
            <InvestAddressRegistrationField
                disabled={state.isLoading}
                isRequired
                name={ADDRESS}
                settings={addressSettings}
                styler={{ marginBottom: 48 }}
            />
            {state.isLoading ? (
                <Aligner>
                    <LoadingBlockDesktop
                        loadingIconSrc={loadingTextIconSrc}
                        loadingText={loadingText}
                    />
                </Aligner>
            ) : (
                <SecurityWarrantyBlock
                    {...securityWarrantyBlockSettings}
                    buttonLoading={state.isLoading}
                />
            )}
        </Form>
    );
};
